import React, { FormEvent, useState } from "react";
import {
  TextInput,
  Autocomplete,
  Group,
  Button,
  Divider,
  Text
} from "@mantine/core";
import { useForm } from "@mantine/form";
import axios from "axios";
import { mtsUrl } from "../../lib/mts-url";
import { ContactFields } from "../../types/contact.types";
import debounce from "../../lib/debounce";
import { IconSearch } from "@tabler/icons-react";

const compareContacts = (a: ContactFields, b: ContactFields) => (
  a.name === b.name && a.email === b.email
);

interface ContactFormProps {
  onSubmit: (contact: ContactFields) => void;
}

export default function ContactForm({ onSubmit }: ContactFormProps) {
  const [acOpen, setAcOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<ContactFields[]>([]);
  const [selected, setSelected] = useState<ContactFields | null>(null);
  const form = useForm<ContactFields>({
    mode: 'uncontrolled',
    initialValues: {
      id: null,
      name: '',
      email: '',
      department: '',
    },
    validate: {
      name: (value) => value.trim().length === 0 ? 'Name is required' : null,
      email: (value) => value.trim().length === 0 ? 'Email is required' : null,
    }
  });

  const fetchSuggestions = debounce(async (query: string) => {
    if (query.trim().length === 0) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(mtsUrl('contacts.json'), {
        params: { q: query },
      });

      setSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    }
  }, 300);

  const onFormSubmit = (contact: ContactFields) => {
    const contactType = !!contact.contact_type ? contact.contact_type : (
      !!contact.id ? 'user' : 'contact'
    );

    const submitContact = {
      id: contact.id,
      name: contact.name,
      email: contact.email,
      department: contact.department,
      contact_type: contactType,
    }

    form.reset();
    setSelected(null);
    setSuggestions([]);
    onSubmit(submitContact);
  };

  const onFormAction = () => {
    const validation = form.validate();
    if (Object.keys(validation.errors).length > 0) return;
    form.onSubmit(onFormSubmit)();
  };

  const onAcOptionSubmit = (val: string) => {
    const cid = Number(val);
    const contact = suggestions.find((s) => s.id === cid);
    if (!contact) return;
    form.setValues({ ...contact, contact_type: 'user' });
    setSelected(contact);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter" && !acOpen) {
      event.preventDefault();
      onFormAction();
    }
  };

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)} onKeyDown={handleKeyDown}>
      <Autocomplete
        label="Search users"
        rightSection={<IconSearch />}
        data={suggestions.map((c) => ({
          value: String(c.id),
          label: c.name || c.username
        }))}
        onChange={(value) => {
          fetchSuggestions(value);

          if (selected && !compareContacts(selected, form.values)) {
            setSelected(null);
            form.setFieldValue('id', null);
          }
        }}
        onOptionSubmit={onAcOptionSubmit}
        onDropdownOpen={() => setAcOpen(true)}
        onDropdownClose={() => setAcOpen(false)}
      />

      <Divider my="xl" size="xs" />

      <Text size="xs">
        If a contact for the provided email already exists, that record will be
        used instead. The name and department may change after saving as a
        result.
      </Text>

      <TextInput
        {...form.getInputProps('name')}
        key={form.key('name')}
        label="Name"
        mt="md"
        required
      />
      <TextInput
        {...form.getInputProps('email')}
        key={form.key('email')}
        label="Email"
        mt="md"
        required
      />
      <TextInput
        {...form.getInputProps('department')}
        key={form.key('department')}
        label="Department"
        mt="md"
      />
      <Group justify="flex-end" mt="sm" p="xs">
        <Button onClick={onFormAction} type="button">
          Add
        </Button>
      </Group>
    </form>
  );
}